<template>
  <div class="tarea-list">
    <!-- Filtros -->
    <div class="filtros">
      <div class="filtro">
        <label for="filterTitulo">Título:</label>
        <input
          type="text"
          id="filterTitulo"
          v-model="filterTitulo"
          placeholder="Buscar por título"
        />
      </div>
      <div class="filtro">
        <label for="filterPersonal">Personal:</label>
        <select id="filterPersonal" v-model="filterPersonal">
          <option value="">Todos</option>
          <option v-for="p in personalList" :key="p.id" :value="p.id">
            {{ p.nombre || p.nombreCompleto || 'Sin Nombre' }} {{ p.apellido || '' }}
          </option>
        </select>
      </div>
      <div class="filtro">
        <label for="filterEstado">Estado:</label>
        <select id="filterEstado" v-model="filterEstado">
          <option value="">Todos</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="COMPLETADA">Completada</option>
          <option value="CANCELADA">Cancelada</option>
        </select>
      </div>
      <div class="filtro">
        <label for="filterFecha">Fecha:</label>
        <input type="date" id="filterFecha" v-model="filterFecha" />
      </div>
      <button class="btn btn-secondary" @click="clearFilters">
        Limpiar Filtros
      </button>
    </div>

    <!-- Mensaje si no hay tareas -->
    <div v-if="sortedTasks.length === 0" class="no-tareas">
      No hay tareas que coincidan con los filtros.
    </div>

    <!-- Tabla de Tareas -->
    <div class="table-responsive" v-else>
      <table class="tareas-table">
        <thead>
          <tr>
            <th @click="sortBy('titulo')" style="cursor:pointer">
              Título <font-awesome-icon :icon="iconoOrden('titulo')" />
            </th>
            <th @click="sortBy('personal.fullname')" style="cursor:pointer">
              Personal <font-awesome-icon :icon="iconoOrden('personal.fullname')" />
            </th>
            <th @click="sortBy('fecha')" style="cursor:pointer">
              Fecha <font-awesome-icon :icon="iconoOrden('fecha')" />
            </th>
            <th>Descripción</th>
            <th @click="sortBy('estado')" style="cursor:pointer">
              Estado <font-awesome-icon :icon="iconoOrden('estado')" />
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="t in paginatedTasks"
            :key="t.id"
            :class="{
              pendiente: t.estado === 'PENDIENTE',
              completada: t.estado === 'COMPLETADA',
              cancelada: t.estado === 'CANCELADA'
            }"
          >
            <td>{{ t.titulo }}</td>
            <td>
              <span v-if="t.personal">
                {{ t.personal.nombre }} {{ t.personal.apellido }}
              </span>
              <span v-else>Sin asignar</span>
            </td>
            <td>
              <span v-if="!t.es_recurrente">{{ formatDate(t.fecha) }}</span>
              <span v-else>{{ getDiaSemana(t.dia_semana) }}</span>
            </td>
            <td>{{ t.descripcion }}</td>
            <td>
              <template v-if="editingEstadoId === t.id">
                <select v-model="newEstado">
                  <option value="PENDIENTE">Pendiente</option>
                  <option value="COMPLETADA">Completada</option>
                  <option value="CANCELADA">Cancelada</option>
                </select>
                <button class="btn btn-submit btn-small" @click="updateTaskEstado(t.id)">
                  Guardar
                </button>
                <button class="btn btn-cancel btn-small" @click="cancelEditEstado">
                  Cancelar
                </button>
              </template>
              <template v-else>
                {{ t.estado }}
              </template>
            </td>
            <td>
              <div class="options-container">
                <button class="btn btn-options" @click="toggleOptions(t)">
                  <font-awesome-icon icon="ellipsis-v" class="icon" />
                </button>
                <div
                  v-if="activeOptionsId === t.id"
                  class="dropdown-options"
                  @mouseleave="activeOptionsId = null"
                >
                  <button class="btn btn-editar" @click="editTask(t)">
                    <font-awesome-icon icon="edit" class="icon" /> Editar
                  </button>
                  <button
                    class="btn btn-info"
                    @click="toggleEditEstado(t)"
                    v-if="editingEstadoId !== t.id"
                  >
                    <font-awesome-icon icon="exchange-alt" class="icon" /> Cambiar Estado
                  </button>
                  <button class="btn btn-eliminar" @click="deleteTask(t.id)">
                    <font-awesome-icon icon="trash-alt" class="icon" /> Eliminar
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <nav v-if="totalPages > 1" class="mt-3">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="currentPage--">Anterior</a>
        </li>
        <li
          class="page-item"
          v-for="page in totalPages"
          :key="page"
          :class="{ active: currentPage === page }"
        >
          <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" href="#" @click.prevent="currentPage++">Siguiente</a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'TareaList',
  data() {
    return {
      tareas: [],
      filterTitulo: '',
      filterPersonal: '',
      filterEstado: '',
      filterFecha: '',
      sortColumn: '',
      sortOrder: 'asc',
      personalList: [],
      diasSemana: [
        'Domingo',
        'Lunes',
        'Martes',
        'Miércoles',
        'Jueves',
        'Viernes',
        'Sábado'
      ],
      editingEstadoId: null,
      newEstado: '',
      activeOptionsId: null, // Id de la tarea cuyo menú de opciones está visible
      // Paginación
      currentPage: 1,
      pageSize: 10,
    };
  },
  computed: {
    filteredTasks() {
      return this.tareas.filter(t => {
        const matchTitulo = this.filterTitulo
          ? t.titulo.toLowerCase().includes(this.filterTitulo.toLowerCase())
          : true;
        const matchPersonal = this.filterPersonal
          ? t.personal && t.personal.id === parseInt(this.filterPersonal)
          : true;
        const matchEstado = this.filterEstado ? t.estado === this.filterEstado : true;
        const matchFecha = this.filterFecha
          ? (!t.es_recurrente &&
             t.fecha &&
             this.formatDate(t.fecha) === new Date(this.filterFecha).toLocaleDateString())
          : true;
        return matchTitulo && matchPersonal && matchEstado && matchFecha;
      });
    },
    sortedTasks() {
      return [...this.filteredTasks].sort((a, b) => {
        // Prioridad: PENDIENTE (0), COMPLETADA (1), CANCELADA (2)
        const priority = {
          'PENDIENTE': 0,
          'COMPLETADA': 1,
          'CANCELADA': 2
        };
        if (priority[a.estado] !== priority[b.estado]) {
          return priority[a.estado] - priority[b.estado];
        }
        // Si tienen el mismo estado y se ha seleccionado columna de ordenamiento
        if (!this.sortColumn) return 0;
        let aVal = this.getNestedValue(a, this.sortColumn);
        let bVal = this.getNestedValue(b, this.sortColumn);
        if (this.sortColumn === 'fecha') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        } else {
          aVal = aVal ? aVal.toString().toLowerCase() : '';
          bVal = bVal ? bVal.toString().toLowerCase() : '';
        }
        if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },
    paginatedTasks() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedTasks.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.sortedTasks.length / this.pageSize);
    }
  },
  methods: {
    async fetchTasks() {
      try {
        const club_id = this.$store.getters.getClubActual.id;
        const response = await axios.get('/api/libreta_tarea/', { params: { club_id } });
        this.tareas = response.data;
      } catch (error) {
        console.error('Error al obtener tareas:', error);
        alert('Error al cargar las tareas.');
      }
    },
    async fetchPersonalList() {
      try {
        const club_id = this.$store.getters.getClubActual.id;
        const { data } = await axios.get('/api/personal', { params: { club_id } });
        this.personalList = data;
      } catch (error) {
        console.error('Error al obtener el personal:', error);
      }
    },
    editTask(tarea) {
      this.$emit('edit-task', tarea);
    },
    async deleteTask(id) {
      if (!confirm('¿Está seguro de eliminar esta tarea?')) return;
      try {
        const club_id = this.$store.getters.getClubActual.id;
        await axios.delete(`/api/libreta_tarea/${id}`, { params: { club_id } });
        alert('Tarea eliminada correctamente.');
        this.fetchTasks();
      } catch (error) {
        console.error('Error al eliminar tarea:', error);
        alert(error.response?.data?.error || 'Error al eliminar la tarea.');
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortOrder = 'asc';
      }
    },
    iconoOrden(column) {
      if (this.sortColumn !== column) return 'sort';
      return this.sortOrder === 'asc' ? 'sort-up' : 'sort-down';
    },
    getNestedValue(obj, key) {
      if (key === 'personal.fullname') {
        if (obj.personal) {
          return (
            (obj.personal.nombre || '') + ' ' + (obj.personal.apellido || '')
          ).toLowerCase();
        }
        return '';
      }
      return key.split('.').reduce((o, i) => (o ? o[i] : null), obj);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString();
      } catch (e) {
        console.error('Error al formatear fecha:', e);
        return dateStr;
      }
    },
    getDiaSemana(index) {
      return this.diasSemana[index] || '';
    },
    toggleEditEstado(tarea) {
      if (this.editingEstadoId === tarea.id) {
        this.editingEstadoId = null;
        this.newEstado = '';
      } else {
        this.editingEstadoId = tarea.id;
        this.newEstado = tarea.estado;
      }
    },
    async updateTaskEstado(id) {
      if (!this.newEstado) return;
      try {
        const club_id = this.$store.getters.getClubActual.id;
        await axios.put(`/api/libreta_tarea_estado/${id}`, { estado: this.newEstado, club_id });
        alert('Estado actualizado.');
        this.editingEstadoId = null;
        this.newEstado = '';
        this.fetchTasks();
      } catch (error) {
        console.error('Error al actualizar estado:', error);
        alert(error.response?.data?.error || 'Error al actualizar el estado.');
      }
    },
    cancelEditEstado() {
      this.editingEstadoId = null;
      this.newEstado = '';
    },
    toggleOptions(tarea) {
      this.activeOptionsId = this.activeOptionsId === tarea.id ? null : tarea.id;
    },
    clearFilters() {
      this.filterTitulo = '';
      this.filterPersonal = '';
      this.filterEstado = '';
      this.filterFecha = '';
      this.fetchTasks();
    }
  },
  mounted() {
    this.fetchTasks();
    this.fetchPersonalList();
  }
};
</script>

<style scoped>
/* Contenedor General */
.tarea-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Filtros */
.filtros {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  align-items: flex-end;
}
.filtro {
  display: flex;
  flex-direction: column;
}
.filtro label {
  font-weight: 500;
  margin-bottom: 5px;
}
.filtro input,
.filtro select {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

/* Estilos para la Tabla */
.tareas-table {
  width: 100%;
  border-collapse: collapse;
}
.tareas-table th,
.tareas-table td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}
.tareas-table th {
  background-color: #f8f9fa;
  cursor: pointer;
  user-select: none;
}
.tareas-table tr:nth-child(even) {
  background-color: #f2f2f2;
}

/* Colores por estado */
.tareas-table tr.pendiente {
  background-color: #fff3cd; /* Amarillo */
}
.tareas-table tr.completada {
  background-color: #d4edda; /* Verde */
}
.tareas-table tr.cancelada {
  background-color: #f8d7da; /* Rojo */
}

/* Botones de Opciones */
.options-container {
  position: relative;
}
.btn-options {
  background-color: #6c757d;
  color: #fff;
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.btn-options:hover {
  background-color: #5a6268;
}
.dropdown-options {
  position: absolute;
  top: 110%;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  z-index: 10;
  display: flex;
  flex-direction: column;
}
.dropdown-options button {
  background: none;
  border: none;
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.dropdown-options button:hover {
  background-color: #f2f2f2;
}

/* Mensaje de "No hay tareas" */
.no-tareas {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 20px;
}

/* Estilos para la paginación */
.pagination {
  margin-top: 20px;
}
</style>
